import { useContext } from 'react'

import { useForm, useLatestEntity, useThunkDispatch } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as geoCountryActions from '@redux/modules/geoCountry'
import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/module'

import defaultState, { requiredFields } from '@models/geoCountry'
import type { GeoCountryModel } from '@models/types'

export const generateUrls = (geoCountry: Partial<GeoCountryModel> = {}) => {
  const { id } = geoCountry

  return {
    editGeoCountryUrl: `#/systemManager/geoCountries/${id}/edit`,
    geoCountriesIndexUrl: '#/systemManager/geoCountries?active=true',
    inactiveGeoCountriesIndexUrl: '#/systemManager/geoCountries?active=false',
  }
}

type CreateGeoCountryParams = {
  dispatch: AppDispatch,
  geoCountryParams: Partial<GeoCountryModel>,
  requestOptions?: EntityOptions,
}

const createGeoCountry = (params: CreateGeoCountryParams) => {
  const { geoCountryParams, dispatch, requestOptions } = params
  const { createGeoCountry: createFn } = geoCountryActions

  return dispatch(createFn(geoCountryParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response

      const { editGeoCountryUrl } = generateUrls(data)

      return {
        data,
        redirectUrl: editGeoCountryUrl,
        success,
      }
    }

    return response
  })
}

type UpdateGeoCountryParams = {
  dispatch: AppDispatch,
  geoCountry: GeoCountryModel,
  geoCountryParams: Partial<GeoCountryModel>,
  requestOptions?: EntityOptions,
}

const updateGeoCountry = (params: UpdateGeoCountryParams) => {
  const {
    geoCountry, geoCountryParams, dispatch, requestOptions,
  } = params
  const { updateGeoCountry: updateFn } = geoCountryActions

  const updatedParams = {
    id: geoCountry.id,
    ...geoCountryParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type AddGeoCountry = {
  geoCountry: GeoCountryModel,
  showCreateGeoCountryModal: Function,
}

const addGeoCountry = (params: AddGeoCountry) => new Promise((resolve, reject) => {
  const { geoCountry, showCreateGeoCountryModal } = params

  if (showCreateGeoCountryModal){
    const payload = {
      geoCountry,
    }

    showCreateGeoCountryModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateGeoCountryModal not defined in PageContext callbacks'))
})

export function useGeoCountryForm(
  geoCountry: Partial<GeoCountryModel> = {},
  options: UseFormOptions = {},
) {
  const { validateOn } = options

  const geoCountryForm = useForm(
    defaultState,
    { entity: geoCountry, requiredFields, validateOn },
    [geoCountry.id, geoCountry.cache_key],
  )

  return {
    ...geoCountryForm,
  }
}

function useGeoCountry(initEntity: Partial<GeoCountryModel> = {}) {
  // @ts-expect-error TS2345
  const { entity: geoCountry } = useLatestEntity(initEntity, 'geoCountries')

  const dispatch = useThunkDispatch()

  const {
    creating, loading, updating,
  } = useSelector(reduxState => reduxState.geoCountries)

  const { callbacks } = useContext(PageContext)
  const { showCreateGeoCountryModal } = callbacks || {}

  return {
    callbacks: {
      // @ts-expect-error TS2322
      addGeoCountry: () => addGeoCountry({ geoCountry, showCreateGeoCountryModal }),
      createGeoCountry: (geoCountryParams: Partial<GeoCountryModel>, requestOptions?: EntityOptions) => (
        createGeoCountry({ geoCountryParams, dispatch, requestOptions })
      ),
      updateGeoCountry: (geoCountryParams: GeoCountryModel, requestOptions?: EntityOptions) => (
        updateGeoCountry({
          // @ts-expect-error TS2322
          geoCountry, geoCountryParams, dispatch, requestOptions,
        })),
    },
    creating,
    geoCountry,
    loading,
    updating,
    // @ts-expect-error TS2345
    urls: generateUrls(geoCountry),
  }
}

export default useGeoCountry
